import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/Skeleton";
import { formatDollarsRound } from "@/customer/catalog/pricing";
import { usePublicTypesByIds } from "@/customer/catalog/api";
import type { Cart, CartItem } from "@/customer/cart/types";
import type { TypeSummary } from "@/customer/catalog/types";

// Read-only cart-contents recap inside the checkout shell. Editing
// happens back on /cart so we don't duplicate the waiver controls.
export function CartContentsSection({ cart }: { cart: Cart }): JSX.Element {
  const ids = Array.from(new Set(cart.payload.items.map((i) => i.type_id)));
  const { data: types = [] } = usePublicTypesByIds(ids);
  const byId = new Map(types.map((t) => [t.id, t]));

  return (
    <div className="space-y-4">
      <ul className="divide-y divide-zinc-100">
        {cart.payload.items.map((item, idx) => (
          <li key={`${item.type_id}-${idx}`} className="flex items-start gap-4 py-3">
            <Thumb type={byId.get(item.type_id)} />
            <ItemBody item={item} type={byId.get(item.type_id)} />
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between text-sm">
        <Link to="/cart" className="text-brand-red underline">
          Edit cart
        </Link>
        <span className="text-zinc-500">
          {cart.payload.items.length} item{cart.payload.items.length === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}

function Thumb({ type }: { type: TypeSummary | undefined }): JSX.Element {
  if (type?.featured_image_url) {
    return (
      <img
        src={type.featured_image_url}
        alt=""
        className="h-14 w-14 flex-none rounded-md object-cover"
      />
    );
  }
  return <div className="h-14 w-14 flex-none rounded-md bg-zinc-100" aria-hidden="true" />;
}

function ItemBody({ item, type }: { item: CartItem; type: TypeSummary | undefined }): JSX.Element {
  const title = type?.name ?? <Skeleton className="h-4 w-32" />;
  return (
    <div className="flex-1 min-w-0">
      <p className="truncate text-sm font-semibold text-brand-black">{title}</p>
      {type?.from_price_per_week_cents ? (
        <p className="text-xs text-zinc-500">
          From {formatDollarsRound(type.from_price_per_week_cents)}/week
        </p>
      ) : null}
      {item.attachment_type_ids.length > 0 || item.damage_waiver ? (
        <p className="mt-1 text-xs text-zinc-500">
          {item.attachment_type_ids.length > 0 ? (
            <>
              {item.attachment_type_ids.length} attachment
              {item.attachment_type_ids.length === 1 ? "" : "s"}
            </>
          ) : null}
          {item.attachment_type_ids.length > 0 && item.damage_waiver ? " · " : null}
          {item.damage_waiver ? "Damage waiver" : null}
        </p>
      ) : null}
    </div>
  );
}
